import { useEffect, useRef } from "react";
import { CampsiteBrandBar } from "@/components/branding/CampsiteBrandBar";
import { formatCurrency } from "@/lib/money";
import type { GuestTabletPosCart, GuestTabletPosCartLine, GuestTabletPosCartPhase } from "@/types/guestTablet";

type Props = {
  posCart: GuestTabletPosCart;
  siteName: string;
  logoUrl?: string | null;
};

function cartPhase(phase: string | undefined): GuestTabletPosCartPhase {
  if (phase === "cash" || phase === "card" || phase === "paid") return phase;
  return "open";
}

function formatQty(value: string | number): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (Number.isInteger(n)) return String(n);
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(n);
}

function qtyTimesPrice(line: GuestTabletPosCartLine, currency: string): string {
  const qty = formatQty(line.quantity);
  if (line.unit_price == null || line.unit_price === "") return `${qty} ×`;
  return `${qty} × ${formatCurrency(line.unit_price, currency)}`;
}

function paymentTitle(phase: GuestTabletPosCartPhase): string {
  if (phase === "cash") return "Barzahlung";
  if (phase === "card") return "Kartenzahlung";
  if (phase === "paid") return "Vielen Dank";
  return "Zuletzt";
}

function paymentHint(phase: GuestTabletPosCartPhase): string {
  if (phase === "cash") return "Bitte den Betrag an der Kasse geben.";
  if (phase === "card") return "Bitte Karte an das Terminal halten.";
  if (phase === "paid") return "Der Einkauf ist bezahlt.";
  return "Die Positionen erscheinen hier, sobald sie gescannt werden.";
}

function footerHint(phase: GuestTabletPosCartPhase): string {
  if (phase === "cash") return "Bar · bitte Betrag bereithalten";
  if (phase === "card") return "Karte · bitte Karte bereithalten";
  if (phase === "paid") return "Vielen Dank";
  return "inkl. MwSt.";
}

export function TabletPosCartOverlay({ posCart, siteName, logoUrl }: Props) {
  const lines = posCart.lines;
  const currency = posCart.currency || "EUR";
  const phase = cartPhase(posCart.phase);
  const requested = posCart.highlight_index;
  const highlightIndex =
    requested != null && requested >= 0 && requested < lines.length
      ? requested
      : Math.max(lines.length - 1, 0);
  const current = lines[highlightIndex];
  const activeRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [highlightIndex, lines, phase]);

  return (
    <div
      className="guest-tablet-pos-display"
      data-phase={phase}
      role="region"
      aria-label="Kundenanzeige Kasse"
    >
      <header className="guest-tablet-pos-display__header">
        <CampsiteBrandBar
          siteName={siteName}
          logoUrl={logoUrl}
          compact
          className="guest-tablet-pos-display__brand"
        />
        <div className="guest-tablet-pos-display__meta">
          <span className="guest-tablet-pos-display__kasse">Kasse</span>
          {posCart.guest_display_name ? (
            <span className="guest-tablet-pos-display__guest">{posCart.guest_display_name}</span>
          ) : null}
        </div>
      </header>

      <div className="guest-tablet-pos-display__hero" aria-live="polite">
        <p className="guest-tablet-pos-display__kicker">{paymentTitle(phase)}</p>
        {phase === "open" && current ? (
          <>
            <h2 className="guest-tablet-pos-display__hero-name">{current.description}</h2>
            <div className="guest-tablet-pos-display__hero-row">
              <span>{qtyTimesPrice(current, currency)}</span>
              <span className="guest-tablet-pos-display__hero-amount">
                {formatCurrency(current.line_total, currency)}
              </span>
            </div>
          </>
        ) : (
          <>
            <p className="guest-tablet-pos-display__hero-amount">
              {formatCurrency(posCart.total_amount, currency)}
            </p>
            <p className="guest-tablet-pos-display__hero-hint">{paymentHint(phase)}</p>
          </>
        )}
      </div>

      <ul className="guest-tablet-pos-display__lines">
        {lines.map((line, index) => {
          const active = index === highlightIndex && phase === "open";
          return (
            <li
              key={`${line.description}-${index}`}
              ref={active ? activeRef : undefined}
              className={
                active
                  ? "guest-tablet-pos-display__line guest-tablet-pos-display__line--active"
                  : "guest-tablet-pos-display__line"
              }
            >
              <span className="guest-tablet-pos-display__qty">{formatQty(line.quantity)}</span>
              <span className="guest-tablet-pos-display__name">
                {line.description}
                {line.unit_price != null && Number(line.quantity) !== 1 ? (
                  <span className="guest-tablet-pos-display__unit">
                    {formatCurrency(line.unit_price, currency)}
                  </span>
                ) : null}
              </span>
              <span className="guest-tablet-pos-display__price">
                {formatCurrency(line.line_total, currency)}
              </span>
            </li>
          );
        })}
      </ul>

      <footer className="guest-tablet-pos-display__footer">
        <div>
          <p className="guest-tablet-pos-display__footer-label">
            {phase === "paid" ? "Bezahlt" : "Zu zahlen"}
          </p>
          <p className="guest-tablet-pos-display__footer-hint">{footerHint(phase)}</p>
        </div>
        <p className="guest-tablet-pos-display__total">
          {formatCurrency(posCart.total_amount, currency)}
        </p>
      </footer>
    </div>
  );
}
